const STORAGE_KEY = 'animationsDisabled';

export class ScrollAnimations {
  private readonly observer: IntersectionObserver;

  constructor() {
    // Zustand aus localStorage wiederherstellen
    if (localStorage.getItem(STORAGE_KEY) === 'true') {
      document.documentElement.classList.add('animations-disabled');
    }

    // Animationen komplett überspringen wenn der User das bevorzugt
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // .js-anims aktiviert die CSS-Animationsregeln. Erst jetzt werden Elemente
    // initial versteckt – kein JS = kein weißer Screen.
    document.documentElement.classList.add('js-anims');

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          // Double-rAF: stellt sicher, dass Safari den initialen "from"-State
          // der @keyframes-Animation gerendert hat, bevor wir play-state
          // auf "running" schalten. Ohne diesen Trick überspringt Safari
          // die Animation beim ersten Paint.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              entry.target.classList.add('is-visible');
            });
          });

          this.observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.05,
        rootMargin: '0px 0px -20px 0px',
      }
    );

    this.applyStagger();
    this.observeAll();
  }

  /** Beobachtet alle Elemente mit [data-animate] */
  private observeAll(): void {
    document.querySelectorAll<HTMLElement>('[data-animate]').forEach((el) => {
      this.observer.observe(el);
    });
  }

  /**
   * Setzt automatisch --animate-delay auf Kinder von [data-animate-stagger].
   * Das Attribut gibt den Delay-Schritt in ms an (z. B. data-animate-stagger="100").
   */
  private applyStagger(): void {
    document
      .querySelectorAll<HTMLElement>('[data-animate-stagger]')
      .forEach((container) => {
        const step = Number(container.getAttribute('data-animate-stagger')) || 100;
        const children = container.querySelectorAll<HTMLElement>('[data-animate]');
        children.forEach((child, i) => {
          child.style.setProperty('--animate-delay', `${i * step}ms`);
        });
      });
  }

  /**
   * Schaltet alle Scroll-Animationen ein oder aus.
   * Schreibt den Zustand in localStorage.
   */
  static toggle(): void {
    const html = document.documentElement;
    const isDisabled = html.classList.toggle('animations-disabled');
    localStorage.setItem(STORAGE_KEY, String(isDisabled));
  }

  /** Gibt zurück ob Animationen aktuell deaktiviert sind. */
  static isDisabled(): boolean {
    return document.documentElement.classList.contains('animations-disabled');
  }
}
